import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiX,
  FiMove,
  FiMinus,
  FiPlus,
  FiSettings,
  FiCode,
  FiRefreshCw,
  FiChevronDown,
  FiChevronRight,
  FiSearch,
  FiFilter
} from 'react-icons/fi';
import {
  ArchitectureVariable,
  getArchitectureVariables,
  deleteVariable as apiDeleteVariable,
  VariableScope
} from '../../api/variables';
import VariableCard from './VariableCard';
import VariableModal from './VariableModal';

interface VariablesPanelProps {
  architectureId: string;
  onClose: () => void;
}

const VariablesPanel: React.FC<VariablesPanelProps> = ({ architectureId, onClose }) => {
  const { t } = useTranslation('canvas');
  const [isOpen, setIsOpen] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState<{ x: number; y: number }>(() => {
    const saved = localStorage.getItem('variablesPanel.position');
    if (saved) {
      return JSON.parse(saved);
    }
    return { x: window.innerWidth - 500, y: 80 };
  });
  const dragRef = useRef<{ startX: number; startY: number; startPosX: number; startPosY: number } | null>(null);

  // State for variables
  const [variables, setVariables] = useState<ArchitectureVariable[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [scopeFilter, setScopeFilter] = useState<VariableScope | 'all'>('all');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['General', 'Infrastructure']));

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingVariable, setEditingVariable] = useState<ArchitectureVariable | null>(null);

  // Fetch variables
  const fetchVariables = useCallback(async () => {
    if (!architectureId) return;

    setLoading(true);
    setError(null);
    try {
      const scope = scopeFilter === 'all' ? undefined : scopeFilter;
      const data = await getArchitectureVariables(architectureId, scope);
      setVariables(data);
    } catch (err: any) {
      console.error('Error fetching variables:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load variables');
    } finally {
      setLoading(false);
    }
  }, [architectureId, scopeFilter]);

  useEffect(() => {
    fetchVariables();
  }, [fetchVariables]);

  // Save position to localStorage
  useEffect(() => {
    localStorage.setItem('variablesPanel.position', JSON.stringify(position));
  }, [position]);

  // Drag handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.drag-handle')) {
      setIsDragging(true);
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startPosX: position.x,
        startPosY: position.y,
      };
      e.preventDefault();
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && dragRef.current) {
        const deltaX = e.clientX - dragRef.current.startX;
        const deltaY = e.clientY - dragRef.current.startY;
        setPosition({
          x: Math.max(0, Math.min(window.innerWidth - 480, dragRef.current.startPosX + deltaX)),
          y: Math.max(0, Math.min(window.innerHeight - 100, dragRef.current.startPosY + deltaY)),
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      dragRef.current = null;
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Filter variables
  const filteredVariables = variables.filter(v => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        v.name.toLowerCase().includes(query) ||
        v.display_name?.toLowerCase().includes(query) ||
        v.description?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Group by category
  const groupedVariables = filteredVariables.reduce((acc, variable) => {
    const category = variable.category || 'General';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(variable);
    return acc;
  }, {} as Record<string, ArchitectureVariable[]>);

  // Sort categories
  const sortedCategories = Object.keys(groupedVariables).sort();

  // Toggle category expansion
  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  // Handle edit
  const handleEdit = (variable: ArchitectureVariable) => {
    setEditingVariable(variable);
    setShowModal(true);
  };

  // Handle delete
  const handleDelete = async (variableId: string) => {
    if (!window.confirm(t('variables.confirmDelete', 'Are you sure you want to delete this variable?'))) {
      return;
    }

    try {
      await apiDeleteVariable(architectureId, variableId);
      await fetchVariables();
    } catch (err: any) {
      console.error('Error deleting variable:', err);
      alert(err.response?.data?.error || 'Failed to delete variable');
    }
  };

  // Handle create/update success
  const handleModalSuccess = () => {
    setShowModal(false);
    setEditingVariable(null);
    fetchVariables();
  };

  // Parameters count
  const parametersCount = variables.filter(v => v.scope === 'parameter').length;
  const internalCount = variables.filter(v => v.scope === 'variable').length;

  // Minimized view
  if (!isOpen) {
    return (
      <div
        style={{
          position: 'fixed',
          left: position.x,
          top: position.y,
          background: '#6366F1',
          color: 'white',
          padding: 12,
          borderRadius: 8,
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
        onClick={() => setIsOpen(true)}
        onMouseDown={handleMouseDown}
      >
        <div className="drag-handle" style={{ cursor: isDragging ? 'grabbing' : 'grab', display: 'flex', alignItems: 'center', gap: 8 }}>
          <FiMove size={16} />
          <FiSettings size={20} />
          <span style={{ fontSize: 14, fontWeight: 500 }}>
            {t('variables.minimized', { count: variables.length })}
          </span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          position: 'fixed',
          left: position.x,
          top: position.y,
          width: 480,
          maxHeight: `calc(100vh - ${position.y + 20}px)`,
          background: 'white',
          borderRadius: 12,
          boxShadow: isDragging ? '0 12px 32px rgba(0, 0, 0, 0.3)' : '0 8px 24px rgba(0, 0, 0, 0.15)',
          zIndex: isDragging ? 1000 : 10,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transition: isDragging ? 'none' : 'box-shadow 0.2s ease',
        }}
        onMouseDown={handleMouseDown}
      >
        {/* Header */}
        <div
          className="drag-handle"
          style={{
            padding: 16,
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
            cursor: isDragging ? 'grabbing' : 'grab',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiMove size={16} style={{ color: 'rgba(255,255,255,0.7)' }} />
            <FiSettings size={20} style={{ color: 'white' }} />
            <div>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'white' }}>
                {t('variables.title', 'Variables & Parameters')}
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>
                {parametersCount} {t('variables.parameters', 'parameters')} | {internalCount} {t('variables.internal', 'internal')}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                fetchVariables();
              }}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                cursor: 'pointer',
                padding: 6,
                display: 'flex',
                alignItems: 'center',
                borderRadius: 4,
              }}
              title={t('variables.refresh', 'Refresh')}
            >
              <FiRefreshCw size={16} style={{ color: 'white' }} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(false);
              }}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                cursor: 'pointer',
                padding: 6,
                display: 'flex',
                alignItems: 'center',
                borderRadius: 4,
              }}
              title={t('variables.minimize', 'Minimize')}
            >
              <FiMinus size={16} style={{ color: 'white' }} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                cursor: 'pointer',
                padding: 6,
                display: 'flex',
                alignItems: 'center',
                borderRadius: 4,
              }}
              title={t('variables.close', 'Close')}
            >
              <FiX size={16} style={{ color: 'white' }} />
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB', background: '#FAFAFA' }}>
          {/* Search */}
          <div style={{ position: 'relative', marginBottom: 12 }}>
            <FiSearch size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
            <input
              type="text"
              placeholder={t('variables.search', 'Search variables...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 38px',
                border: '1px solid #E5E7EB',
                borderRadius: 8,
                fontSize: 14,
                outline: 'none',
              }}
            />
          </div>

          {/* Filters and Add button */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1 }}>
              <FiFilter size={14} style={{ color: '#6B7280' }} />
              <select
                value={scopeFilter}
                onChange={(e) => setScopeFilter(e.target.value as VariableScope | 'all')}
                style={{
                  padding: '6px 8px',
                  border: '1px solid #E5E7EB',
                  borderRadius: 6,
                  fontSize: 13,
                  background: 'white',
                  cursor: 'pointer',
                }}
              >
                <option value="all">{t('variables.filterAll', 'All')}</option>
                <option value="parameter">{t('variables.filterParameters', 'Parameters')}</option>
                <option value="variable">{t('variables.filterVariables', 'Variables')}</option>
              </select>
            </div>

            <button
              onClick={() => {
                setEditingVariable(null);
                setShowModal(true);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                background: '#6366F1',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#4F46E5'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#6366F1'}
            >
              <FiPlus size={16} />
              {t('variables.add', 'Add')}
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: 16 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9CA3AF' }}>
              <FiRefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
              <p style={{ marginTop: 12 }}>{t('variables.loading', 'Loading...')}</p>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#EF4444' }}>
              <p>{error}</p>
              <button
                onClick={fetchVariables}
                style={{
                  marginTop: 12,
                  padding: '8px 16px',
                  background: '#FEE2E2',
                  color: '#DC2626',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
              >
                {t('variables.retry', 'Retry')}
              </button>
            </div>
          ) : filteredVariables.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9CA3AF' }}>
              <FiCode size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
              <p style={{ fontSize: 14, margin: 0 }}>
                {searchQuery
                  ? t('variables.noResults', 'No variables found matching your search')
                  : t('variables.empty', 'No variables defined yet')}
              </p>
              {!searchQuery && (
                <button
                  onClick={() => {
                    setEditingVariable(null);
                    setShowModal(true);
                  }}
                  style={{
                    marginTop: 16,
                    padding: '10px 20px',
                    background: '#6366F1',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontSize: 14,
                  }}
                >
                  {t('variables.createFirst', 'Create your first variable')}
                </button>
              )}
            </div>
          ) : (
            sortedCategories.map((category) => (
              <div key={category} style={{ marginBottom: 16 }}>
                {/* Category header */}
                <div
                  onClick={() => toggleCategory(category)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    background: '#F3F4F6',
                    borderRadius: 8,
                    cursor: 'pointer',
                    marginBottom: 8,
                  }}
                >
                  {expandedCategories.has(category) ? (
                    <FiChevronDown size={16} style={{ color: '#6B7280' }} />
                  ) : (
                    <FiChevronRight size={16} style={{ color: '#6B7280' }} />
                  )}
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                    {category}
                  </span>
                  <span style={{
                    fontSize: 11,
                    padding: '2px 8px',
                    background: '#E5E7EB',
                    borderRadius: 10,
                    color: '#6B7280',
                  }}>
                    {groupedVariables[category].length}
                  </span>
                </div>

                {/* Variables list */}
                {expandedCategories.has(category) && (
                  <div style={{ display: 'grid', gap: 8 }}>
                    {groupedVariables[category]
                      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                      .map((variable) => (
                        <VariableCard
                          key={variable.id}
                          variable={variable}
                          onEdit={() => handleEdit(variable)}
                          onDelete={() => handleDelete(variable.id)}
                        />
                      ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal for create/edit */}
      {showModal && (
        <VariableModal
          architectureId={architectureId}
          variable={editingVariable}
          onClose={() => {
            setShowModal(false);
            setEditingVariable(null);
          }}
          onSuccess={handleModalSuccess}
        />
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default VariablesPanel;
